import React, { useState } from 'react';
import { Button, Tooltip, makeStyles, shorthands } from '@fluentui/react-components';
import { Copy16Regular, Checkmark16Regular } from '@fluentui/react-icons';
import { customColors } from '../../../../styles';

const useClasses = makeStyles({
    container: {
        position: 'relative',
        maxWidth: '800px',
        maxHeight: '500px',
        marginTop: '10px',
        marginBottom: '10px',
    },
    preContainer: {
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100%',
        maxHeight: '400px',
        ...shorthands.margin('0px'),
        ...shorthands.padding('10px'),
        ...shorthands.borderRadius('5px'),
        overflowX: 'auto',
        overflowY: 'auto',
        backgroundColor: '#f5f5f5',
        color: customColors.summarizeButtonDarker,
        cursor: 'text',
    },
    buttonContainer: {
        display: 'flex',
        flexDirection: 'row',
        position: 'absolute',
        right: '6px',
    },
    codeLanguageArea: {
        color: '#fff',
        backgroundColor: customColors.summarizeButtonDarker,
        borderTopLeftRadius: '5px',
        borderTopRightRadius: '5px',
        paddingLeft: '10px',
        paddingRight: '10px',
        paddingTop: '2px',
        paddingBottom: '2px',
    },
    copyButton: {
        position: 'absolute',
        right: '12px',
        zIndex: 1,
        cursor: 'pointer',
    },
    successCheckMark: {
        color: 'green',
        fontWeight: 'bold',
    },
});

interface OutputPreBlockProps {
    children?: React.ReactNode;
}

export const OutputPreBlock: React.FC<OutputPreBlockProps> = ({ children }) => {
    const classes = useClasses();
    const [copySuccess, setCopySuccess] = useState(false);
    const [isHovered, setHovered] = useState(true);

    const codeElement = children as React.ReactElement<{ className?: string; children?: React.ReactNode }>;
    const languageClassName = (codeElement?.props?.className as string) ?? '';
    const languageMatch = /language-(\w+)/.exec(languageClassName);
    const language = languageMatch ? languageMatch[1] : '';

    const handleCopyToClipboard = () => {
        const text = codeElement?.props?.children?.toString() ?? '';
        navigator.clipboard
            .writeText(text)
            .then(() => {
                setCopySuccess(true);
                setTimeout(() => setCopySuccess(false), 2000);
            })
            .catch((err) => {
                console.error('Failed to copy to clipboard', err);
                setCopySuccess(false);
            });
    };

    return (
        <div className={classes.container} onMouseOver={() => setHovered(true)} onMouseOut={() => setHovered(false)}>
            {language && <div className={classes.codeLanguageArea}>{language}</div>}
            <pre className={classes.preContainer}>{children}</pre>
            {isHovered && (
                <div className={classes.buttonContainer} style={{ top: language ? '30px' : '7px' }}>
                    <Tooltip content="Copy Code" relationship="label">
                        <Button
                            icon={
                                copySuccess ? (
                                    <Checkmark16Regular className={classes.successCheckMark} />
                                ) : (
                                    <Copy16Regular />
                                )
                            }
                            onClick={handleCopyToClipboard}
                            onMouseOver={() => setHovered(true)}
                            onMouseOut={() => setHovered(false)}
                        />
                    </Tooltip>
                </div>
            )}
        </div>
    );
};
